import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LanguageSwitcher from './LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const NavContainer = styled.nav`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    padding: 15px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavActions = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HomeLink = styled(Link)`
    display: flex;
    align-items: center;
    gap: 10px;
    color: white;
    text-decoration: none;
    font-weight: 600;
    font-size: 1.1rem;
    padding: 8px 16px;
    border-radius: 20px;
    transition: all 0.3s ease;
    background: rgba(255, 255, 255, 0.1);

    &:hover {
        background: rgba(255, 255, 255, 0.2);
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 6px 12px;
        gap: 6px;
    }
`;

const HomeIcon = styled.span`
    font-size: 1.2rem;
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.08);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 20px;
    font-weight: 600;
    font-size: 1rem;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;

    &:hover {
        background: rgba(255, 255, 255, 0.18);
        transform: translateY(-2px);
    }

    &:active {
        transform: translateY(0);
    }

    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 6px 12px;
        gap: 6px;
    }
`;

// 需要從 home 導航過來才顯示 nav bar 的頁面路徑
const PAGES_REQUIRING_FROM_HOME = ['/music', '/word-game'];

const normalizePath = (pathname) => {
    if (!pathname) return '/';
    return pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;
};

// 檢查是否應該隱藏 nav bar
const shouldHideNavBar = (pathname, fromHome) => {
    const normalized = normalizePath(pathname);

    // 在首頁不顯示導航
    if (normalized === '/' || normalized === '/home') {
        return true;
    }

    // Charley Memory 所有子頁面都隱藏導航
    if (normalized.startsWith('/charley-memory')) {
        return true;
    }

    // 在特定頁面：只有從 home 導航過來才顯示，直接訪問 URL 不顯示
    if (PAGES_REQUIRING_FROM_HOME.includes(normalized)) {
        return !fromHome;
    }

    return false;
};

const Navigation = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const fromHome = location.state?.fromHome;

    // 檢查是否應該隱藏 nav bar
    if (shouldHideNavBar(location.pathname, fromHome)) {
        return null;
    }

    const handleBack = () => {
        // 如果有可回退的歷史紀錄就返回上一頁，否則回首頁
        const historyIndex = window.history.state?.idx;
        if (historyIndex && historyIndex > 0) {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <NavContainer>
            <NavActions>
                <BackButton onClick={handleBack}>
                    <span>⬅️</span>
                    <span>{t('nav.back')}</span>
                </BackButton>
                <HomeLink to="/">
                    <HomeIcon>🏠</HomeIcon>
                    <span>{t('nav.home')}</span>
                </HomeLink>
            </NavActions>
            <LanguageSwitcher inline={true} />
        </NavContainer>
    );
};

export default Navigation;

